import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Save, Check, Search, CircleMinus, CircleCheck } from 'lucide-react';
import Skeleton from '../../common/Skeleton';
import FormControl from '../../common/FormControl';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import Pagination from '../../common/Pagination';
import Toast, { ToastMessage } from '../../common/Toast';
import { confirmWarning, confirmActive } from '../../common/confirm';
import { ProcedureTemplate, FormTemplateItem, formTemplateService } from '../../../services/auditService';
import { extractApiError } from '../../../utils/apiError';

interface ProcedureFormProps {
    procedure: ProcedureTemplate;
}

const FIELD_TYPES = [
    'Text', 'Date', 'Number', 'Decimal', 'Percentage', 'Numeric01',
    'Email', 'PAN', 'File Upload', 'Dropdown',
];
const FIELD_TYPE_OPTIONS = FIELD_TYPES.map(t => ({ value: t, label: t }));

interface FormState {
    form_question: string;
    answer_data_type: string;
    is_required: boolean;
    options: string;
}

const blank = (): FormState => ({ form_question: '', answer_data_type: 'Text', is_required: false, options: '' });

const ProcedureForm: React.FC<ProcedureFormProps> = ({ procedure }) => {
    const [items, setItems] = useState<FormTemplateItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [toggling, setToggling] = useState(false);
    const [isModalOpen, setModalOpen] = useState(false);
    const [currentField, setCurrentField] = useState<FormTemplateItem | null>(null);
    const [formData, setFormData] = useState<FormState>(blank);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => { setPage(1); }, [searchTerm, pageSize]);

    useEffect(() => {
        if (isModalOpen) {
            setSubmitting(false);
            setToggling(false);
        }
    }, [isModalOpen, currentField]);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const list = await formTemplateService.listForProcedure(procedure.id);
            setItems(list);
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load form fields.') });
        } finally {
            setLoading(false);
        }
    }, [procedure.id]);

    useEffect(() => { reload(); }, [reload]);

    const handleAdd = useCallback(() => {
        setCurrentField(null);
        setFormData(blank());
        setModalOpen(true);
    }, []);

    const handleEdit = (item: FormTemplateItem) => {
        setCurrentField(item);
        setFormData({ form_question: item.form_question, answer_data_type: item.answer_data_type, is_required: !!item.is_required, options: item.options || '' });
        setModalOpen(true);
    };

    const handleToggleActive = async () => {
        if (!currentField) return;
        const isActive = (currentField.is_active ?? 1) === 1;
        const next = isActive ? 0 : 1;
        const confirmFn = isActive ? confirmWarning : confirmActive;
        const ok = await confirmFn({
            title: next ? 'Reactivate this form field?' : 'Inactivate this form field?',
            text: next
                ? 'It will appear in active lists again.'
                : 'It will be hidden from active lists.',
            confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
        });
        if (!ok) return;
        setToggling(true);
        try {
            await formTemplateService.update(currentField.id, { is_active: next });
            setToast({ type: next ? 'success' : 'warning', message: next ? 'Reactivated' : 'Inactivated' });
            setModalOpen(false);
            await reload();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to update status.') });
        } finally {
            setToggling(false);
        }
    };

    const handleSave = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!formData.form_question.trim()) return;
        if (formData.answer_data_type === 'Dropdown' && !formData.options.trim()) {
            setToast({ type: 'warning', message: 'Dropdown fields need at least one option.' });
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                form_question: formData.form_question,
                answer_data_type: formData.answer_data_type,
                is_required: formData.is_required,
                options: formData.answer_data_type === 'Dropdown' ? formData.options : null,
            };
            if (currentField) {
                await formTemplateService.update(currentField.id, payload);
                setToast({ type: 'success', message: 'Form field updated.' });
            } else {
                await formTemplateService.create({ procedure_template_id: procedure.id, is_active: 1, ...payload });
                setToast({ type: 'success', message: 'Form field created.' });
            }
            setModalOpen(false);
            await reload();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Save failed.') });
        } finally {
            setSubmitting(false);
        }
    };

    const filtered = items.filter(f => f.form_question.toLowerCase().includes(searchTerm.toLowerCase()));
    const totalRecords = filtered.length;
    const paginatedRows = filtered.slice((page - 1) * pageSize, page * pageSize);

    const isEditMode = !!currentField;
    const isFieldActive = (currentField?.is_active ?? 1) === 1;

    return (
        <>
            <div className="netbody-header">
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        type="text"
                        placeholder="Search…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_form_template_search"
                    />
                    <Button variant="fill" icon={Plus} data-testid="BTN_form_template_create" onClick={handleAdd}>Create</Button>
                </div>
            </div>

            <div className="table-list flex-1 min-h-0">
                <div className="table-list-scroll">
                    <table className="table-list-table">
                        <thead>
                            <tr>
                                <th className="table-list-th table-list-cell--sno">S.No</th>
                                <th className="table-list-th">Field Label</th>
                                <th className="table-list-th">Type</th>
                                <th className="table-list-th">Required</th>
                            </tr>
                        </thead>
                        <tbody aria-busy={loading} aria-live="polite">
                            {loading && <Skeleton.TableRows cols={['3xs', 'md', 'pill', 'pill']} />}
                            {!loading && filtered.length === 0 && (
                                <tr><td colSpan={4} className="table-list-empty-cell">No fields defined yet. Click "Create" to add one.</td></tr>
                            )}
                            {!loading && paginatedRows.map((f, idx) => (
                                <tr key={f.id} className="table-list-row" data-testid={`BTN_form_template_row_${f.id}_open`} onClick={() => handleEdit(f)}>
                                    <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                    <td className="table-list-cell">
                                        <span className="table-list-cell-title">{f.form_question}</span>
                                        {f.answer_data_type === 'Dropdown' && f.options && (
                                            <span className="table-list-cell-sub block mt-0.5">Options: {f.options}</span>
                                        )}
                                    </td>
                                    <td className="table-list-cell">
                                        <span className="status-badge xs neutral">{f.answer_data_type}</span>
                                    </td>
                                    <td className="table-list-cell">
                                        {f.is_required ? (
                                            <span className="status-badge xs danger">Yes</span>
                                        ) : (
                                            <span className="text-gray-400 text-xs">No</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalRecords={totalRecords}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    recordLabel="fields"
                />
            </div>

            <Modal isOpen={isModalOpen} onClose={() => setModalOpen(false)} size="md">
                <Modal.Header>
                    {isEditMode
                        ? <>Editing "<span className="item-highlight">{currentField!.form_question}</span>"</>
                        : 'Create Form Field'}
                </Modal.Header>
                <Modal.Body>
                    <form id="form-field-form" data-testid="MOD_form_template_form" onSubmit={handleSave} className="flex flex-col gap-y-4">
                        <FormControl.Input
                            label="Field Label"
                            required
                            data-testid="INP_form_template_label"
                            value={formData.form_question}
                            onChange={e => setFormData({ ...formData, form_question: e.target.value })}
                            placeholder="e.g. Invoice Amount"
                            autoFocus
                        />
                        <FormControl.SearchSelect
                            label="Field Type"
                            data-testid="SEL_form_template_type"
                            options={FIELD_TYPE_OPTIONS}
                            value={formData.answer_data_type}
                            onChange={v => setFormData({ ...formData, answer_data_type: String(v ?? 'Text') })}
                        />
                        {formData.answer_data_type === 'Dropdown' && (
                            <div>
                                <FormControl.Textarea
                                    label="Options"
                                    required
                                    data-testid="INP_form_template_options"
                                    placeholder="Enter options separated by commas (e.g. IT, HR, Finance)"
                                    value={formData.options}
                                    onChange={e => setFormData({ ...formData, options: e.target.value })}
                                />
                                <p className="text-xs text-gray-500 mt-1">Separate multiple options with commas.</p>
                            </div>
                        )}
                        <FormControl.Checkbox
                            label="Mark as Required Field"
                            data-testid="CHK_form_template_required"
                            checked={formData.is_required}
                            onChange={checked => setFormData({ ...formData, is_required: checked })}
                        />
                    </form>
                </Modal.Body>
                <Modal.Footer className={isEditMode ? 'app-modal-footer--split' : ''}>
                    {isEditMode && (
                        <Button
                            variant={isFieldActive ? 'inactive-outline' : 'active-outline'}
                            icon={isFieldActive ? CircleMinus : CircleCheck}
                            data-testid="BTN_form_template_toggle_active"
                            onClick={handleToggleActive}
                            disabled={toggling || submitting}
                        >
                            {toggling ? '...' : isFieldActive ? 'Inactive' : 'Active'}
                        </Button>
                    )}
                    <Button
                        type="submit"
                        form="form-field-form"
                        variant={isEditMode ? 'update' : 'fill'}
                        icon={isEditMode ? Check : Save}
                        data-testid="BTN_form_template_submit"
                        disabled={submitting || toggling || !formData.form_question.trim() || (formData.answer_data_type === 'Dropdown' && !formData.options.trim())}
                    >
                        {submitting
                            ? isEditMode ? 'Updating...' : 'Submitting...'
                            : isEditMode ? 'Update' : 'Submit'}
                    </Button>
                </Modal.Footer>
            </Modal>
            <Toast toast={toast} onClose={() => setToast(null)} />
        </>
    );
};

export default ProcedureForm;
